import { useCallback, ReactNode } from 'react';
import { EntitlementsApiService, EntitlementsResponse } from '../services/api';
import { EntitlementsContext, type UseEntitlementsResult } from './useEntitlements.context';
import { useAsyncData } from './useAsyncData';

// Type-only re-export — erased at compile time, so it does NOT break Fast
// Refresh (the `react-refresh/only-export-components` rule allows it).
// Runtime consumers (the `useEntitlements` hook itself) must import from
// `./useEntitlements.context` directly.
export type { UseEntitlementsResult } from './useEntitlements.context';

/** Default permissive features — used when the API fails or returns no data. */
const ALL_FEATURES_ENABLED: EntitlementsResponse['features'] = {
    maxAcknowledgmentsPerPeriod: null,
    maxActivePolicies: null,
    maxAdminUsers: null,
    maxTotalUsers: null,
    groupsEnabled: true,
    recurrenceEnabled: true,
    ssoEnabled: true,
    scheduledRemindersEnabled: true,
    analyticsEnabled: true,
    auditLogEnabled: true,
    dataRetentionDays: null,
    accountIsolation: false,
    adminDigestEnabled: true,
    templatesEnabled: true,
    versioningEnabled: true,
    scimEnabled: true,
};

/**
 * Provider that fetches the current tenant's entitlements exactly once per
 * authenticated session and shares the result with every consumer via context.
 * Mirrors `CurrentUserProvider`. Falls back to all features enabled on API
 * error (backward compat). Mount this alongside `CurrentUserProvider` so all
 * entitlement-gated pages share a single fetch instead of each calling the
 * endpoint independently.
 */
export function EntitlementsProvider({ children }: { children: ReactNode }) {
    const fetchEntitlements = useCallback(() => EntitlementsApiService.getEntitlements(), []);
    const { data, error, loading, refetch: reload } = useAsyncData<EntitlementsResponse>('entitlements', fetchEntitlements);

    // The error branch is unconditional and takes precedence over any data
    // still in hand: a failed refresh drops back to the permissive fallback
    // rather than keeping the tenant on the last-loaded (possibly restrictive)
    // plan, which is what the pre-#3423 catch block did.
    const failed = error !== null;

    const tier = failed ? 'premium' : (data?.tier ?? null);
    const features = failed ? ALL_FEATURES_ENABLED : (data?.features ?? null);
    /* Defensive: older entitlement records can omit the counter, in which case
       the API returns it as undefined. Coerce to 0 so the UI never renders an
       empty numerator in PlanUsagePanel's "Acknowledgments this period" row.
       (#1369) */
    const currentPeriodAcknowledgments = failed ? 0 : (data?.currentPeriodAcknowledgments ?? 0);
    const periodStartDate = failed ? null : (data?.periodStartDate ?? null);
    const userPackQuantity = failed ? undefined : data?.userPackQuantity;
    // Absent → false (hide the Manage Billing button) per #2070, but true on
    // error so a transient API failure doesn't hide it from a Standard-tier
    // tenant who has a real Stripe customer. Today's behavior: click → 404 →
    // toast.
    const hasBillingAccount = failed ? true : (data?.hasBillingAccount ?? false);

    /**
     * Always a SILENT reload: nothing here ever set `loading` back to true, so
     * a plan refresh must not flash the skeleton on every entitlement-gated
     * surface. Stable across re-renders, so consumers can list it in deps.
     */
    const refetch = useCallback((): Promise<void> => reload({ silent: true }), [reload]);

    const value: UseEntitlementsResult = {
        loading,
        tier,
        features,
        currentPeriodAcknowledgments,
        periodStartDate,
        userPackQuantity,
        hasBillingAccount,
        refetch,
    };

    return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}
